import { useIntl } from 'react-intl';

import Divider from 'components/divider';
import { Link } from 'components/router';
import { Heading, Text } from 'components/typography';

import cx from 'classnames';
import { FilterableListItem } from './index';

import CheckIcon from 'icons/CheckIcon';

const HASH = '#';

type ListFilterBlockProps = {
  title: string;
  list: FilterableListItem[];
  isLast: boolean;
  linkTemplate?: (slug: string) => string;
  onItemClick?: (
    objectSelected: FilterableListItem,
    e: React.MouseEvent
  ) => void;
  selectedIndexes: number[];
};

export default function FilterableListBlock({
  title,
  list,
  isLast,
  linkTemplate,
  onItemClick,
  selectedIndexes
}: ListFilterBlockProps): React.ReactNode {
  const intl = useIntl();
  const groupAriaText =
    title === HASH
      ? intl.formatMessage({ defaultMessage: 'Other items' })
      : intl.formatMessage(
          {
            defaultMessage: 'Group {title}'
          },
          { title }
        );

  const renderItem = (
    item: FilterableListItem,
    index: number,
    linkTemplate?: (value: unknown) => string,
    onItemClick?: (objectSelected: unknown, e: React.MouseEvent) => void
  ) => {
    const isSelected = selectedIndexes.includes(index);

    const itemClasses = cx(
      'block inline text-left font-bold capitalize hover:underline hover-none:no-underline',
      { 'text-blue-500': isSelected }
    );

    return linkTemplate ? (
      <Link href={linkTemplate(item.value)} prefetch={false}>
        <Text size="m" className={itemClasses}>
          {item.label}
        </Text>
      </Link>
    ) : (
      <>
        <button
          type="button"
          className={itemClasses}
          onClick={e => {
            if (onItemClick) onItemClick(item.value, e);
          }}
        >
          <Text size="m">{item.label}</Text>
        </button>

        {isSelected && (
          <CheckIcon variant="small" className="ml-1 inline h-4 w-4" />
        )}
      </>
    );
  };

  return (
    <>
      <div
        className="flex flex-col items-start py-6 md:pb-10 md:pt-4 lg:flex-row"
        role="group"
        tabIndex={-1}
        aria-label={groupAriaText}
      >
        <Heading
          size="m"
          className="shrink-0 pb-4 pt-2 lg:sticky lg:top-0 lg:w-1/5 lg:pb-0"
        >
          {title}
        </Heading>
        <ul className="column-count-2 md:column-count-3 h-max w-full lg:w-4/5">
          {list.map((item, index) => {
            return (
              <li
                key={JSON.stringify(item.value)}
                className="inline-block w-full py-2"
              >
                {renderItem(item, index, linkTemplate, onItemClick)}
              </li>
            );
          })}
        </ul>
      </div>
      {!isLast && <Divider />}
    </>
  );
}
